import { useState } from "react";
import { ChevronDown } from "lucide-react";

type Promise = { title: string; body: string };

type Props = {
  promises: Promise[];
};

const shades = [
  "bg-primary/30",
  "bg-primary/45",
  "bg-primary/60",
  "bg-primary/80",
  "bg-primary",
] as const;

export function PromiseScroll({ promises }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <ol className="mt-10 overflow-hidden rounded-[4px]">
      {promises.map((p, i) => {
        const shade = shades[i] ?? shades[shades.length - 1];
        const num = String(i + 1).padStart(2, "0");
        const isOpen = openIndex === i;
        const panelId = `promise-panel-${i}`;
        return (
          <li
            key={p.title}
            className={`group/promise relative transition-[background-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:cursor-pointer ${shade}`}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-4 px-6 py-5 text-left md:gap-6 md:px-10 md:py-6 lg:grid-cols-[1fr_auto] lg:cursor-default"
            >
              <h3 className="font-display text-lg font-extrabold leading-tight text-white transition-colors duration-300 md:text-2xl">
                {p.title}
              </h3>
              <span className="font-display text-4xl font-extrabold leading-none text-brand-button transition-colors duration-300 md:text-6xl lg:order-last">
                {num}
              </span>
              <ChevronDown
                aria-hidden
                className={`h-6 w-6 text-white transition-transform duration-300 lg:hidden ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <div
              id={panelId}
              className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:grid-rows-[0fr] lg:group-hover/promise:grid-rows-[1fr] ${
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div
                className={`overflow-hidden transition-opacity duration-[400ms] ease-out lg:opacity-0 lg:group-hover/promise:opacity-100 ${
                  isOpen ? "opacity-100" : "opacity-0"
                }`}
              >
                <p className="max-w-3xl px-6 pb-6 font-body text-sm leading-relaxed text-white md:px-10 md:pb-8 md:text-base">
                  {p.body}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
